import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  ExternalLink, MapPin, AlertTriangle, Info, RefreshCw, ChevronDown, FileText, Search, Copy, Wrench
} from 'lucide-react';
import { LOKASYON_SAYFALARI } from './europcarSeoPlan';
import {
  SABIHA_URL, SABIHA_META, SABIHA_BLOKLAR, SABIHA_TEYIT, SABIHA_TEKNIK,
  SABIHA_HEDEF_SORGULAR, SABIHA_KELIME, sabihaMetniDuzMetin
} from './europcarSabihaMetni';

/**
 * Lokasyon sayfası iş listesi — hangi ofis sayfasına önce içerik yazılacak.
 *
 * Sayfalar "kaçırılan tık" ile sıralanır: gösterim × (hedef CTR − mevcut CTR).
 * Hedef CTR sektör ortalaması değil, sitenin kendi ilk-5 sıradaki marka dışı
 * CTR'ıdır ve backend'de canlı hesaplanır. Her satır açıldığında o sayfaya inen
 * gerçek sorgular listelenir; içerik brief'i budur.
 *
 * Backend: GET /api/location-pages. Kelime hacimleri Keyword Planner anlık
 * görüntüsü (backend src/data/europcar_keywords.json), sıralama ve sayfa
 * eşleşmesi canlı Search Console.
 *
 * Sadece Europcar brand'inde görünür (App.js allowBrandIds + Sidebar
 * restrictedBrands). Sayfa yalnız açık tema — dark: varyantı bilerek yok.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const dShort = (s) => (s ? new Date(s).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' }) : '—');

/** Crawl envanteri (kelime sayısı / sayfa tipi) URL ile eşleşir. */
const ENVANTER = new Map(LOKASYON_SAYFALARI.map(l => [l.url.replace(/\/+$/, ''), l]));
const envOf = (url) => ENVANTER.get(String(url || '').replace(/\/+$/, '')) || null;

const TIP_ETIKET = {
  havalimani: 'havalimanı',
  'ilce-tatil-ofisi': 'ilçe / tatil',
  'sehir-merkez': 'şehir merkez',
  sehir: 'şehir sayfası',
  'tren-gari': 'tren garı',
  avm: 'AVM'
};

/**
 * Niyet başına önerilen H2. Metinler öneridir — sayfada da öyle etiketli.
 * Hangi niyetin önerileceği veriden gelir: sayfanın kendi marka dışı
 * sorgularında o niyet yoksa, kategoride varsa, yazılacak başlık odur.
 */
const BASLIK_ONERI = {
  sartlar: 'Kiralama şartları: sürücü yaşı, ehliyet kıdemi, depozito ve istenen belgeler',
  fiyat: 'Fiyatlar: günlük ücret neye göre değişir, en uygun rezervasyon ne zaman yapılır',
  sure: 'Günlük, haftalık ve aylık kiralama — hangi süre kime uygun',
  aracTipi: 'Bu ofiste bulunan araç sınıfları (otomatik vites, SUV, ticari, elektrikli)',
  teslim: 'Teslim alma ve iade: ofis nerede, çalışma saatleri, ofis dışı teslim',
  soforlu: 'Şoförlü kiralama ve transfer hizmeti',
  kurumsal: 'Kurumsal ve filo kiralama'
};

const TIP_ONERI = {
  havalimani: [
    'Terminalden ofise nasıl gidilir (yürüme mesafesi / shuttle / kat bilgisi)',
    'Uçuş rötarında araç ne kadar süre bekletilir',
    'Gece geç saatte iniş ve 7/24 teslim'
  ],
  'tren-gari': [
    'Gar çıkışından ofise yürüme tarifi',
    'Tren saatine göre teslim ve iade'
  ],
  'ilce-tatil-ofisi': [
    'Bölgede araçla gezilecek rotalar ve mesafeler',
    'Sezon dışı / sezon içi müsaitlik'
  ],
  'sehir-merkez': [
    'Şehir içi park, otopark ve trafik notları',
    'Ofise toplu taşımayla ulaşım'
  ]
};

function Stat({ label, value, sub, tone }) {
  return (
    <div className="bg-white p-4">
      <div className={`text-[26px] font-bold leading-none tabular-nums ${
        tone === 'emerald' ? 'text-emerald-700' : tone === 'rose' ? 'text-rose-600' : 'text-slate-900'}`}>
        {value}
      </div>
      <div className="mt-1.5 text-[13px] text-slate-500">{label}</div>
      {sub ? <div className="mt-0.5 text-[12px] text-slate-400">{sub}</div> : null}
    </div>
  );
}

function SectionHead({ eyebrow, title, children }) {
  return (
    <div className="mb-4">
      <h2 className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">{eyebrow}</h2>
      <p className="mt-1.5 text-[22px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
      {children ? <p className="mt-1.5 max-w-[74ch] text-[14px] leading-relaxed text-slate-500">{children}</p> : null}
    </div>
  );
}

/** Bir lokasyon sayfası — kapalıyken tek satır, açıkken içerik brief'i. */
function SayfaSatiri({ p, sira, niyetAd, acik, onToggle, maxKacan }) {
  const env = envOf(p.url);
  const kelime = env?.kelime ?? null;
  const tip = env?.tip || null;
  const ad = env?.ad || decodeURIComponent(p.yol.split('/').filter(Boolean).pop() || p.yol);
  const icerikYok = kelime != null && kelime < 300;

  const nb = p.sorgular.filter(s => !s.marka);
  const nbImp = nb.reduce((s, q) => s + q.imp, 0) || 1;
  const niyetVar = new Set(Object.entries(p.niyet).filter(([, v]) => v / nbImp >= 0.01).map(([k]) => k));
  const eksik = Object.keys(BASLIK_ONERI).filter(k => !niyetVar.has(k));

  return (
    <div className={`border-b border-slate-100 last:border-0 ${acik ? 'bg-emerald-50/30' : ''}`}>
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-emerald-50/50"
      >
        <span className="w-7 flex-none text-[12px] tabular-nums text-slate-400">{sira}</span>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-[13.5px] font-medium text-slate-800">{ad}</span>
            {tip ? (
              <span className="flex-none rounded bg-slate-100 px-1.5 py-0.5 text-[10.5px] text-slate-500">
                {TIP_ETIKET[tip] || tip}
              </span>
            ) : null}
            {icerikYok ? (
              <span className="flex-none rounded bg-rose-100 px-1.5 py-0.5 text-[10.5px] font-medium text-rose-700">
                metin yok
              </span>
            ) : null}
          </span>
          <span className="mt-0.5 block truncate text-[11.5px] text-slate-400">{p.yol}</span>
        </span>

        <span className="hidden w-[92px] flex-none text-right text-[12.5px] tabular-nums text-slate-500 sm:block">
          {nf(p.imp)}
        </span>
        <span className="hidden w-[64px] flex-none text-right text-[12.5px] tabular-nums text-slate-500 md:block">
          {nf(p.clk)}
        </span>
        <span className="hidden w-[56px] flex-none text-right text-[12.5px] tabular-nums text-slate-500 md:block">
          %{p.ctr.toFixed(2)}
        </span>
        <span className="hidden w-[48px] flex-none text-right text-[12.5px] tabular-nums text-slate-500 lg:block">
          {p.pos}
        </span>

        <span className="w-[132px] flex-none">
          <span className="flex items-center justify-end gap-2">
            <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-slate-100 sm:block">
              <span
                className="block h-full rounded-full bg-emerald-500"
                style={{ width: `${maxKacan ? Math.max(2, 100 * p.kacan / maxKacan) : 0}%` }}
              />
            </span>
            <span className="w-[52px] text-right text-[13px] font-semibold tabular-nums text-emerald-700">
              {nf(p.kacan)}
            </span>
          </span>
        </span>

        <ChevronDown className={`h-4 w-4 flex-none text-slate-300 transition-transform ${acik ? 'rotate-180' : ''}`} />
      </button>

      {acik ? (
        <div className="border-t border-emerald-100 bg-white px-3 py-4 sm:px-5">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12.5px] text-slate-500">
            <a href={p.url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 font-medium text-emerald-700 hover:text-emerald-800">
              Sayfayı aç <ExternalLink className="h-3 w-3" />
            </a>
            <span>Gövde metni: <b className={icerikYok ? 'text-rose-600' : 'text-slate-700'}>
              {kelime == null ? 'ölçülmedi' : `${nf(kelime)} kelime`}</b></span>
            <span>Marka dışı: <b className="text-slate-700">{nf(p.markaDisiImp)}</b></span>
            <span>Marka: <b className="text-slate-700">{nf(p.markaImp)}</b></span>
            <span>Toplam sorgu: <b className="text-slate-700">{nf(p.sorguSayisi)}</b></span>
          </div>

          <div className="mt-4 grid gap-5 lg:grid-cols-[1.35fr_1fr]">
            <div>
              <p className="mb-1.5 text-[12px] font-bold uppercase tracking-wider text-slate-400">
                Bu sayfaya inen sorgular
              </p>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full border-collapse">
                  <tbody>
                    {p.sorgular.slice(0, 20).map(q => (
                      <tr key={q.sorgu} className="border-b border-slate-100 last:border-0">
                        <td className="py-1.5 pl-2.5 pr-2">
                          <span className={`text-[13px] ${q.marka ? 'text-slate-400' : 'text-slate-800'}`}>
                            {q.sorgu}
                          </span>
                          {q.marka ? (
                            <span className="ml-1.5 rounded bg-slate-100 px-1 py-0.5 text-[10px] text-slate-400">marka</span>
                          ) : q.niyet !== 'cekirdek' ? (
                            <span className="ml-1.5 rounded bg-emerald-50 px-1 py-0.5 text-[10px] text-emerald-700">
                              {niyetAd[q.niyet] || q.niyet}
                            </span>
                          ) : null}
                        </td>
                        <td className="w-[70px] py-1.5 pr-2 text-right text-[12.5px] tabular-nums text-slate-500">{nf(q.imp)}</td>
                        <td className="w-[52px] py-1.5 pr-2 text-right text-[12.5px] tabular-nums text-slate-500">{nf(q.clk)}</td>
                        <td className="w-[46px] py-1.5 pr-2.5 text-right text-[12.5px] tabular-nums text-slate-400">{q.pos}</td>
                      </tr>
                    ))}
                    {p.sorgular.length === 0 ? (
                      <tr><td className="p-3 text-[13px] text-slate-400">Bu pencerede sorgu verisi yok.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {p.sorguSayisi > 20 ? (
                <p className="mt-1.5 text-[12px] text-slate-400">
                  En çok gösterim alan 20 sorgu gösteriliyor; sayfanın toplam {nf(p.sorguSayisi)} farklı sorgusu var.
                </p>
              ) : null}
            </div>

            <div>
              <p className="mb-1.5 text-[12px] font-bold uppercase tracking-wider text-slate-400">
                Yazılacak başlıklar <span className="font-medium normal-case tracking-normal text-slate-300">(öneri)</span>
              </p>
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <p className="text-[12.5px] leading-relaxed text-slate-500">
                  Sayfanın kendi sorgularında <b className="text-slate-700">karşılığı olmayan</b> niyetler.
                  Sayfa o konuda metin taşımadığı için o aramalarda hiç görünmüyor.
                </p>
                <ul className="mt-2.5 space-y-1.5">
                  {(TIP_ONERI[tip] || []).map(t => (
                    <li key={t} className="flex gap-2 text-[13px] leading-snug text-slate-700">
                      <span className="mt-[7px] h-1 w-1 flex-none rounded-full bg-emerald-500" />{t}
                    </li>
                  ))}
                  {eksik.map(k => (
                    <li key={k} className="flex gap-2 text-[13px] leading-snug text-slate-700">
                      <span className="mt-[7px] h-1 w-1 flex-none rounded-full bg-emerald-500" />{BASLIK_ONERI[k]}
                    </li>
                  ))}
                </ul>
                {niyetVar.size ? (
                  <p className="mt-3 border-t border-slate-200 pt-2.5 text-[12px] leading-relaxed text-slate-400">
                    Sayfanın hâlihazırda karşıladığı niyetler:{' '}
                    {[...niyetVar].map(k => niyetAd[k] || k).join(', ')}.
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Yayına hazır sayfa metni — şimdilik yalnız Sabiha Gökçen.
 * İçerik `europcarSabihaMetni.js`'te; buradaki iş sadece göstermek ve kopyalatmak.
 */
function HazirMetin() {
  const kopyala = (metin, ad) => {
    navigator.clipboard.writeText(metin)
      .then(() => toast.success(`${ad} kopyalandı`))
      .catch(() => toast.error('Kopyalanamadı'));
  };

  return (
    <section className="mt-12">
      <SectionHead
        eyebrow="Hazır Metin"
        title="Sabiha Gökçen — yayına hazır sayfa metni"
      >
        Listenin 1. sırasındaki sayfa için yazıldı. Metindeki her rakam canlı kaynaklıdır: ofis bilgileri sayfanın
        kendisinden, yaş / depozito / km / iptal koşulları <span className="whitespace-nowrap">/yardim/europcar-kiralama-kosullari</span>{' '}
        sayfasından (11 Ağustos 2026 çekimi). Uydurma fiyat, uydurma vaat yok.
      </SectionHead>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          onClick={() => kopyala(sabihaMetniDuzMetin(), 'Metin')}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-700"
        >
          <Copy className="h-3.5 w-3.5" /> Metni kopyala
        </button>
        <a
          href={SABIHA_URL} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-medium text-slate-600 hover:bg-slate-50"
        >
          Mevcut sayfayı aç <ExternalLink className="h-3.5 w-3.5" />
        </a>
        <span className="ml-auto text-[12px] tabular-nums text-slate-400">
          {nf(SABIHA_KELIME)} kelime · mevcut gövde 253 kelime
        </span>
      </div>

      {/* Title / description önerisi */}
      <div className="mb-4 grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 md:grid-cols-2">
        {[
          { ad: 'Title', oneri: SABIHA_META.title, mevcut: SABIHA_META.mevcutTitle },
          { ad: 'Description', oneri: SABIHA_META.desc, mevcut: SABIHA_META.mevcutDesc }
        ].map(m => (
          <div key={m.ad} className="bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">{m.ad} — önerilen</span>
              <button
                onClick={() => kopyala(m.oneri, m.ad)}
                className="text-slate-300 hover:text-emerald-700"
                title="Kopyala"
              ><Copy className="h-3.5 w-3.5" /></button>
            </div>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-800">{m.oneri}</p>
            <p className="mt-2 border-t border-slate-100 pt-2 text-[12px] leading-relaxed text-slate-400">
              <b className="font-medium text-slate-500">Bugün:</b> {m.mevcut}
            </p>
          </div>
        ))}
      </div>

      {/* Metnin kendisi */}
      <article className="rounded-xl border border-slate-200 bg-white px-5 py-6 sm:px-8 sm:py-8">
        <h3 className="text-[24px] font-bold leading-tight tracking-tight text-slate-900">{SABIHA_META.h1}</h3>

        {SABIHA_BLOKLAR.map((b, i) => {
          if (b.tip === 'h2') {
            return (
              <h4 key={i} className="mt-7 text-[17px] font-bold leading-snug tracking-tight text-slate-900">
                {b.metin}
              </h4>
            );
          }
          if (b.tip === 'p') {
            const teyit = b.metin.startsWith('[TEYİT');
            return (
              <p
                key={i}
                className={`mt-3 max-w-[78ch] text-[14.5px] leading-[1.75] ${
                  teyit
                    ? 'rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900'
                    : 'text-slate-700'}`}
              >{b.metin}</p>
            );
          }
          if (b.tip === 'ul') {
            return (
              <ul key={i} className="mt-3 max-w-[78ch] space-y-1.5">
                {b.ogeler.map(o => (
                  <li key={o} className="flex gap-2.5 text-[14.5px] leading-[1.7] text-slate-700">
                    <span className="mt-[10px] h-1 w-1 flex-none rounded-full bg-emerald-500" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            );
          }
          if (b.tip === 'tablo') {
            return (
              <div key={i} className="mt-4 max-w-[78ch]">
                <p className="mb-1.5 text-[12.5px] font-medium text-slate-500">{b.baslik}</p>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full min-w-[420px] border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        {b.basliklar.map(h => (
                          <th key={h} className="p-2.5 text-left text-[11.5px] font-semibold uppercase tracking-wider text-slate-500">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {b.satirlar.map(s => (
                        <tr key={s[0]} className="border-b border-slate-100 last:border-0">
                          {s.map((h, j) => (
                            <td key={j} className={`p-2.5 text-[13.5px] ${
                              j === 0 ? 'font-medium text-slate-800' : 'tabular-nums text-slate-600'}`}>
                              {h}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          }
          if (b.tip === 'sss') {
            return (
              <dl key={i} className="mt-3 max-w-[78ch] space-y-3.5">
                {b.ogeler.map(o => (
                  <div key={o.s}>
                    <dt className="text-[14.5px] font-semibold leading-snug text-slate-900">{o.s}</dt>
                    <dd className="mt-1 text-[14.5px] leading-[1.75] text-slate-700">{o.c}</dd>
                  </div>
                ))}
              </dl>
            );
          }
          return null;
        })}
      </article>

      {/* Hedeflenen sorgular */}
      <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr className="border-b-2 border-slate-800 bg-slate-50">
              <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Metnin hedeflediği sorgu</th>
              <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Gösterim</th>
              <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tık</th>
              <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Poz</th>
              <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Metinde nerede karşılanıyor</th>
            </tr>
          </thead>
          <tbody>
            {SABIHA_HEDEF_SORGULAR.map(q => (
              <tr key={q.q} className="border-b border-slate-100 last:border-0">
                <td className="p-2.5 text-[13.5px] font-medium text-slate-800">{q.q}</td>
                <td className="p-2.5 text-right text-[13px] tabular-nums text-slate-600">{nf(q.imp)}</td>
                <td className="p-2.5 text-right text-[13px] tabular-nums text-slate-500">{nf(q.clk)}</td>
                <td className={`p-2.5 text-right text-[13px] tabular-nums ${
                  q.pos == null ? 'text-slate-300' : q.pos <= 5 ? 'text-emerald-700' : 'text-amber-600'}`}>
                  {q.pos == null ? '—' : q.pos}
                </td>
                <td className="p-2.5 text-[12.5px] text-slate-500">{q.nerede}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-1.5 text-[12px] text-slate-400">
        Search Console, bu sayfaya inen sorgular, son 40 gün. Poz "—" olanlarda sayfa o sorguda bugün hiç görünmüyor.
      </p>

      {/* Teyit + teknik */}
      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <p className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Yayına almadan önce Europcar'dan teyit
          </p>
          <ul className="mt-2.5 space-y-2">
            {SABIHA_TEYIT.map(t => (
              <li key={t} className="flex gap-2.5 text-[13px] leading-relaxed text-amber-900">
                <span className="mt-[8px] h-1 w-1 flex-none rounded-full bg-amber-500" />{t}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-slate-500">
            <Wrench className="h-3.5 w-3.5" /> Metinle birlikte yapılacak teknik işler
          </p>
          <ul className="mt-2.5 space-y-2.5">
            {SABIHA_TEKNIK.map(t => (
              <li key={t.is} className="text-[13px] leading-relaxed">
                <span className="font-semibold text-slate-800">{t.is}</span>
                <span className="block text-slate-500">{t.not}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export default function EuropcarLocationPages() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [acik, setAcik] = useState(null);
  const [tipFiltre, setTipFiltre] = useState('all');
  const [sadeceBos, setSadeceBos] = useState(false);
  const [hepsi, setHepsi] = useState(false);
  const [kwFiltre, setKwFiltre] = useState('all');
  const [kwHepsi, setKwHepsi] = useState(false);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/location-pages?days=${days}&_t=${Date.now()}`, {
        credentials: 'include', headers
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Veri yüklenemedi');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, days]);

  useEffect(() => { load(); }, [load]);

  const niyetAd = useMemo(
    () => Object.fromEntries((data?.niyetler || []).map(n => [n.key, n.ad])),
    [data]
  );

  const sayfalar = data?.sayfalar || [];
  const filtreli = useMemo(() => sayfalar.filter(p => {
    const env = envOf(p.url);
    if (tipFiltre !== 'all' && env?.tip !== tipFiltre) return false;
    if (sadeceBos && !(env?.kelime != null && env.kelime < 300)) return false;
    return true;
  }), [sayfalar, tipFiltre, sadeceBos]);

  const maxKacan = filtreli[0]?.kacan || 0;
  const gosterilen = hepsi ? filtreli : filtreli.slice(0, 30);

  const kelimeler = data?.kelimeler || [];
  const kwFiltreli = kwFiltre === 'all' ? kelimeler : kelimeler.filter(k => k.sinif === kwFiltre);
  const kwGosterilen = kwHepsi ? kwFiltreli : kwFiltreli.slice(0, 30);

  const metinsiz = sayfalar.filter(p => { const e = envOf(p.url); return e?.kelime != null && e.kelime < 300; });
  const metinsizKacan = metinsiz.reduce((s, p) => s + p.kacan, 0);

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">Europcar · Lokasyon Sayfaları</h1>
          <p className="mt-1.5 text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            Hangi ofis sayfasına önce içerik yazılmalı
          </p>
          <p className="mt-1.5 max-w-[74ch] text-[14px] leading-relaxed text-slate-500">
            Her lokasyon sayfası kaçırılan tıka göre sıralı. Kaçırılan tık = gösterim × (hedef CTR − mevcut CTR);
            hedef CTR dışarıdan bir sektör ortalaması değil, sitenin kendi ilk-5 sıradaki marka dışı CTR'ı.
            Satırı açınca o sayfaya inen gerçek sorgular çıkıyor — içerik brief'i budur.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-200">
            {[30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-[13px] ${days === d ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >{d} gün</button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </button>
        </div>
      </div>

      {data?.pencere?.bas ? (
        <p className="mt-3 text-[12.5px] text-slate-400">
          {dShort(data.pencere.bas)} – {dShort(data.pencere.bit)} · Search Console'un son dolu gününden geriye
          {' '}{data.pencere.gun} gün · hedef CTR %{data.hedefCtr}
        </p>
      ) : null}

      {!brandId ? (
        <p className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-[13.5px] text-amber-800">
          Marka seçili değil.
        </p>
      ) : null}

      {/* ── Özet ─────────────────────────────────────────────────────────── */}
      {data ? (
        <section className="mt-6">
          <div className="grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
            <Stat label="TR lokasyon sayfası" value={nf(data.ozet.sayfa)} sub="/konumlar, yurt dışı kovası hariç" />
            <Stat label="gösterim" value={nf(data.ozet.imp)} sub={`${nf(data.ozet.clk)} tık`} />
            <Stat label="kaçırılan tık" value={nf(data.ozet.kacan)} tone="emerald"
                  sub={`hedef CTR %${data.hedefCtr}`} />
            <Stat label="metni olmayan sayfa" value={nf(metinsiz.length)} tone="rose"
                  sub="gövde < 300 kelime" />
            <Stat label="metinsiz sayfaların kaybı" value={nf(metinsizKacan)} tone="rose"
                  sub={data.ozet.kacan ? `toplamın %${Math.round(100 * metinsizKacan / data.ozet.kacan)}'i` : '—'} />
          </div>

          {data.kapsam != null ? (
            <p className="mt-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12.5px] leading-relaxed text-slate-500">
              <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400" />
              <span>
                Sayfa toplamları sayfa raporundan, sorgu listeleri sayfa+sorgu raporundan geliyor. Search Console
                anonimleştirme yüzünden ikincisini eksik verir: burada kapsam <b className="text-slate-700">%{data.kapsam}</b>.
                Yani bir sayfanın sorgu satırlarının toplamı, o sayfanın gösteriminden düşük olacaktır — veri hatası değil.
              </span>
            </p>
          ) : null}
        </section>
      ) : null}

      {/* ── Öncelik listesi ──────────────────────────────────────────────── */}
      {data ? (
        <section className="mt-10">
          <SectionHead eyebrow="Öncelik Sırası" title="Lokasyon sayfaları, kaçırılan tıka göre">
            Yukarıdaki sayfa en çok kaçan tıka sahip olandır — yani içerik yazıldığında en çok kazandıracak yer.
            Yüksek gösterim + düşük CTR + kazanılabilir pozisyon üçlüsü aynı metrikte birleşiyor.
          </SectionHead>

          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap overflow-hidden rounded-lg border border-slate-200">
              {[['all', 'Hepsi'], ['havalimani', 'Havalimanı'], ['sehir', 'Şehir sayfası'],
                ['ilce-tatil-ofisi', 'İlçe / tatil'], ['sehir-merkez', 'Şehir merkez'], ['tren-gari', 'Tren garı']]
                .map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => { setTipFiltre(k); setAcik(null); }}
                    className={`px-3 py-1.5 text-[13px] ${tipFiltre === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >{label}</button>
                ))}
            </div>
            <button
              onClick={() => { setSadeceBos(v => !v); setAcik(null); }}
              className={`rounded-lg border px-3 py-1.5 text-[13px] ${
                sadeceBos ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              Sadece metni olmayanlar
            </button>
            <span className="ml-auto inline-flex items-center gap-1 text-[12px] tabular-nums text-slate-400">
              <MapPin className="h-3.5 w-3.5" />{filtreli.length} sayfa
            </span>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-3 border-b-2 border-slate-800 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <span className="w-7 flex-none">#</span>
              <span className="min-w-0 flex-1">Sayfa</span>
              <span className="hidden w-[92px] flex-none text-right sm:block">Gösterim</span>
              <span className="hidden w-[64px] flex-none text-right md:block">Tık</span>
              <span className="hidden w-[56px] flex-none text-right md:block">CTR</span>
              <span className="hidden w-[48px] flex-none text-right lg:block">Poz</span>
              <span className="w-[132px] flex-none text-right">Kaçan tık</span>
              <span className="h-4 w-4 flex-none" />
            </div>

            {gosterilen.map((p, i) => (
              <SayfaSatiri
                key={p.url}
                p={p}
                sira={i + 1}
                niyetAd={niyetAd}
                maxKacan={maxKacan}
                acik={acik === p.url}
                onToggle={() => setAcik(a => (a === p.url ? null : p.url))}
              />
            ))}
            {!gosterilen.length ? (
              <p className="p-4 text-[13.5px] text-slate-400">Bu filtreye uyan sayfa yok.</p>
            ) : null}
          </div>

          {filtreli.length > 30 ? (
            <button
              onClick={() => setHepsi(v => !v)}
              className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-emerald-700 hover:text-emerald-800"
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${hepsi ? 'rotate-180' : ''}`} />
              {hepsi ? 'Kısalt' : `Tümünü göster (${filtreli.length})`}
            </button>
          ) : null}

          <p className="mt-3 flex items-start gap-2 max-w-[86ch] text-[12.5px] leading-relaxed text-slate-400">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-500" />
            <span>
              "Yazılacak başlıklar" bir öneridir, ölçüm değil: sayfanın kendi sorgularında karşılığı olmayan
              niyetlerden türetiliyor. Bir niyetin sorgularda görünmemesi, o konuda arama olmadığı anlamına gelmez —
              sayfa o metni taşımadığı için o aramalarda hiç görünmüyor olabilir. Metinler yazılmadan önce Europcar
              tarafından ofis gerçeğiyle (çalışma saati, teslim noktası, filo) doğrulanmalı.
            </span>
          </p>
        </section>
      ) : null}

      {/* ── Kelime havuzu ────────────────────────────────────────────────── */}
      {data && kelimeler.length ? (
        <section className="mt-12">
          <SectionHead
            eyebrow="Kelime Havuzu"
            title="Araç kiralama kategorisinde en çok aranan 100 kelime ve indiği sayfa"
          >
            Hacimler Google Keyword Planner'dan (Türkiye, Türkçe, yalnız Arama ağı); sıralama, pozisyon ve iniş
            sayfası canlı Search Console'dan. Lokasyon kelimeleri bilerek listede tutuldu — kategorinin talebi
            büyük ölçüde şehir ve havalimanı adlarıyla geliyor.
          </SectionHead>

          {data.kelimeMeta && !data.kelimeMeta.hata ? (
            <div className="mb-3 grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="taranan benzersiz kelime" value={nf(data.kelimeMeta.toplam)}
                    sub="3 tohum grubu, Keyword Planner" />
              <Stat label="lokasyonlu kelime" value={nf(data.kelimeMeta.lokasyon)}
                    sub={`jenerik: ${nf(data.kelimeMeta.jenerik)}`} />
              <Stat label="elenen rakip / model" value={nf(data.kelimeMeta.rakip + data.kelimeMeta.model)}
                    sub={`rakip ${nf(data.kelimeMeta.rakip)} · model ${nf(data.kelimeMeta.model)}`} />
              <Stat label="yazım varyantı birleştirildi" value={nf(data.kelimeMeta.havuzKova)}
                    sub={`${nf(data.kelimeMeta.havuzHam)} yazımdan kova sayısı`} />
            </div>
          ) : null}

          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="flex overflow-hidden rounded-lg border border-slate-200">
              {[['all', 'Hepsi'], ['lokasyon', 'Lokasyonlu'], ['jenerik', 'Jenerik']].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setKwFiltre(k)}
                  className={`px-3 py-1.5 text-[13px] ${kwFiltre === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >{label}</button>
              ))}
            </div>
            <span className="ml-auto inline-flex items-center gap-1 text-[12px] tabular-nums text-slate-400">
              <Search className="h-3.5 w-3.5" />{kwFiltreli.length} kelime
            </span>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[860px] border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-800 bg-slate-50">
                  <th className="w-10 p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">#</th>
                  <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Kelime</th>
                  <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Aylık arama</th>
                  <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Gösterim</th>
                  <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tık</th>
                  <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Poz</th>
                  <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">İndiği sayfa</th>
                </tr>
              </thead>
              <tbody>
                {kwGosterilen.map((k, i) => (
                  <tr key={k.kw} className="border-b border-slate-100 last:border-0 hover:bg-emerald-50/40">
                    <td className="p-2.5 text-[12px] tabular-nums text-slate-400">{i + 1}</td>
                    <td className="p-2.5">
                      <span className="text-[13.5px] font-medium text-slate-800">{k.kw}</span>
                      {k.sinif === 'lokasyon' ? (
                        <span className="ml-1.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10.5px] text-emerald-700">lokasyon</span>
                      ) : null}
                      {k.varyant?.length ? (
                        <span className="ml-1.5 text-[11px] text-slate-400" title={k.varyant.join(' · ')}>
                          +{k.varyant.length} yazım
                        </span>
                      ) : null}
                    </td>
                    <td className="p-2.5 text-right text-[13px] font-semibold tabular-nums text-slate-800">{nf(k.vol)}</td>
                    <td className="p-2.5 text-right text-[13px] tabular-nums text-slate-500">{nf(k.imp)}</td>
                    <td className="p-2.5 text-right text-[13px] tabular-nums text-slate-500">{nf(k.clk)}</td>
                    <td className={`p-2.5 text-right text-[13px] tabular-nums ${
                      k.pos == null ? 'text-slate-300' : k.pos <= 5 ? 'text-emerald-700' : k.pos <= 12 ? 'text-amber-600' : 'text-rose-600'}`}>
                      {k.pos == null ? '—' : k.pos}
                    </td>
                    <td className="max-w-[300px] p-2.5">
                      {k.sayfa ? (
                        <a href={k.sayfa} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-start gap-1 text-[12.5px] text-slate-600 hover:text-emerald-700"
                           title={k.sayfa}>
                          <span className="truncate">
                            {k.sayfa.replace(/^https?:\/\/[^/]+/, '') || '/'}
                          </span>
                          {k.nSayfa > 1 ? (
                            <span className="flex-none text-slate-400">·%{k.sayfaPay} ({k.nSayfa} sayfa)</span>
                          ) : null}
                        </a>
                      ) : (
                        <span className="text-[12.5px] text-rose-600">bu kelimede hiç görünmüyoruz</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {kwFiltreli.length > 30 ? (
            <button
              onClick={() => setKwHepsi(v => !v)}
              className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-emerald-700 hover:text-emerald-800"
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${kwHepsi ? 'rotate-180' : ''}`} />
              {kwHepsi ? 'Kısalt' : `Tümünü göster (${kwFiltreli.length})`}
            </button>
          ) : null}

          <p className="mt-3 flex items-start gap-2 max-w-[86ch] text-[12.5px] leading-relaxed text-slate-400">
            <FileText className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400" />
            <span>
              Keyword Planner hacimleri kova ortalamasıdır, tam sayı değildir; aynı kovaya düşen yazım varyantları
              ("araç kiralama" / "arac kira") tek satırda birleştirildi, sayısı satırda yazıyor. Rakip markası, araç
              modeli ve Europcar'ın kendi adını içeren kelimeler listeden çıkarıldı. "İndiği sayfa" o kelimenin en çok
              gösterim aldığı adres; birden fazla sayfa yarışıyorsa payı ve sayfa sayısı yanında.
            </span>
          </p>
        </section>
      ) : null}

      <HazirMetin />

      {loading && !data ? (
        <p className="mt-8 text-[13.5px] text-slate-400">Yükleniyor…</p>
      ) : null}
    </div>
  );
}
